import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  CAlert, CBadge, CButton, CCard, CCardBody, CCardHeader, CListGroup, CListGroupItem, CSpinner,
} from '@coreui/react';
import { api, problemMessage, type Schemas } from '../api/client';
import { useMe } from '../auth/AuthGate';
import { StateBadge } from '../components/StateBadge';
import { formatDate, formatDateTime } from '../format';
import { startTour, startTourOnce } from '../tour/tour';

type MyWork = Schemas['MyWorkResponse'];
type WorkItem = Schemas['MyWorkItemResponse'];

/**
 * Domovská obrazovka. Odpovídá na dvě otázky, které má člověk každé ráno — a nováček
 * úplně poprvé: „co mám udělat já" a „na co čekám u ostatních".
 * <p>
 * Dřív se tu ukazovalo, <em>proč</em> je zakázka moje (přiřazený / založil jsem). To je
 * pravda, ale nic neříká: kdo zakázku vidí poprvé, potřebuje vědět, <em>kdo je na tahu</em>.
 */
export function MyWorkPage() {
  const me = useMe();
  const [work, setWork] = useState<MyWork | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.GET('/api/v1/my/work').then(({ data, error: problem }) => {
      if (data) {
        setWork(data);
      } else {
        setError(problemMessage(problem));
      }
    });
  }, []);

  // Pruvodce az nad nactenym obsahem - kroky ukazuji na skutecne seznamy.
  // ?pruvodce=1 posila tlacitko "Průvodce" z hlavicky z jine stranky.
  // Kdo jeste nema roli, toho nejdriv vita uvitani - prohlidka pocka, az skonci.
  useEffect(() => {
    if (!work) return;
    if (params.get('pruvodce') === '1') {
      setParams({}, { replace: true });
      window.setTimeout(() => startTour('home'), 300);
    } else if (me.roles.length > 0) {
      startTourOnce('home', me.memberId);
    }
  }, [work]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const afterWelcome = () => startTourOnce('home', me.memberId);
    window.addEventListener('contracts:welcome-done', afterWelcome);
    return () => window.removeEventListener('contracts:welcome-done', afterWelcome);
  }, [me.memberId]);

  if (error) {
    return <CAlert color="danger">{error}</CAlert>;
  }

  if (!work) {
    return <CSpinner color="primary" />;
  }

  const myTurn = work.contracts.filter((c) => c.reasons.includes('MyTurn'));
  const watching = work.contracts.filter((c) => !c.reasons.includes('MyTurn'));

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-4 gap-3 flex-wrap">
        <h2 className="mb-0">Moje práce</h2>
        <CButton color="primary" as={Link} to="/zakazky/nova" data-tour="new-contract">Nová zakázka</CButton>
      </div>

      {/* Bez nalepek nedorazi dotaz mireny na roli ani kontrola vedeni - pri psani happy path to na tomhle spadlo. */}
      {me.roles.length === 0 && (
        <CAlert color="warning">
          Nemáte nastavené, co děláte. Práce pro role (třeba kontrola zakázek pro vedení) vám proto nechodí.{' '}
          <Link to="/nastaveni">Nastavit role</Link>
        </CAlert>
      )}


      <CCard className="mb-4" data-tour="my-turn">
        <CCardHeader><strong>Na tahu jste vy</strong>{myTurn.length > 0 && <span className="text-body-secondary"> ({myTurn.length})</span>}</CCardHeader>
        {myTurn.length === 0 ? (
          <CCardBody className="text-body-secondary">Teď na vás žádná zakázka nečeká.</CCardBody>
        ) : (
          <CListGroup flush>
            {myTurn.map((item) => <WorkRow key={item.contract.id} item={item} mine />)}
          </CListGroup>
        )}
      </CCard>

      {work.openQuestions.length > 0 && (
        <CCard className="mb-4" data-tour="questions">
          <CCardHeader><strong>Dotazy a úkoly pro vás</strong></CCardHeader>
          <CListGroup flush>
            {work.openQuestions.map((q) => (
              <CListGroupItem key={q.id} as={Link} to={`/zakazky/${q.contractId}`} className="list-group-item-action">
                <div className="d-flex justify-content-between gap-3">
                  <div>
                    <CBadge color={q.kind === 'Task' ? 'primary' : 'secondary'} className="me-2">
                      {q.kind === 'Task' ? 'úkol' : 'dotaz'}
                    </CBadge>
                    {q.blocksProgress && <CBadge color="danger" className="me-2">blokuje</CBadge>}
                    {q.body}
                  </div>
                  <span className="text-body-secondary small text-nowrap">{formatDateTime(q.askedAt)}</span>
                </div>
                <div className="small text-body-secondary mt-1">
                  {q.contractNumber} {q.contractTitle} · ptá se {q.askedBy}
                </div>
              </CListGroupItem>
            ))}
          </CListGroup>
        </CCard>
      )}

      <CCard data-tour="watching">
        <CCardHeader>
          <strong>Čekáte na ostatní</strong>
        </CCardHeader>
        {watching.length === 0 ? (
          <CCardBody className="text-body-secondary">Na nic nečekáte.</CCardBody>
        ) : (
          <CListGroup flush>
            {watching.map((item) => <WorkRow key={item.contract.id} item={item} />)}
          </CListGroup>
        )}
      </CCard>
    </>
  );
}

/** Jeden řádek: co se má udělat (nebo na koho se čeká), a pod tím o jakou zakázku jde. */
function WorkRow({ item, mine }: { item: WorkItem; mine?: boolean }) {
  const { contract } = item;
  const step = contract.nextStep;

  return (
    <CListGroupItem as={Link} to={`/zakazky/${contract.id}`} className="list-group-item-action">
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <div>
          {step && (mine
            ? <div className="fw-semibold text-primary">{step.action} →</div>
            : <div>Čeká se na: <strong>{step.waitingOn}</strong> <span className="text-body-secondary">— {step.action.toLowerCase()}</span></div>)}
          <div className="mt-1">
            <span className="fw-semibold">{contract.contractNumber}</span> {contract.title}
            <span className="text-body-secondary"> · </span><span className="fw-semibold">{contract.customer.name}</span>
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {contract.isBlocked && <CBadge color="danger">blokováno</CBadge>}
          <StateBadge state={contract.state} />
          <span className={`small text-nowrap ${contract.isOverdue ? 'text-danger fw-semibold' : 'text-body-secondary'}`}>
            {contract.isOverdue ? 'po termínu ' : 'do '}{formatDate(contract.dueDate)}
          </span>
        </div>
      </div>
    </CListGroupItem>
  );
}
